import React, { useState, useEffect } from 'react';
import { Package, Droplet, Building2, Box } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { DownloadCatalogButton } from './DownloadCatalogButton';

export const CategoriesSection = () => {
  const { t, language, direction } = useLanguage();
  const [industrialImageIndex, setIndustrialImageIndex] = useState(0);
  const [boxImageIndex, setBoxImageIndex] = useState(0);

  // Animation for Industrial section - alternate between 2 images
  const industrialImages = [
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/f0kn0zi2_ChatGPT%20Image%20Feb%2018%2C%202026%2C%2007_05_08%20AM.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/dxsi40rn_ChatGPT%20Image%20Feb%2018%2C%202026%2C%2007_23_04%20AM.png'
  ];

  // Animation for Olion Box section - alternate between 2 images
  const boxImages = [
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/v0j7gqgf_Gemini_Generated_Image_3fmym63fmym63fmy.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/knl0ix3j_WhatsApp%20Image%202026-02-08%20at%2008.28.21.jpeg'
  ];

  useEffect(() => {
    const industrialInterval = setInterval(() => {
      setIndustrialImageIndex((prev) => (prev + 1) % industrialImages.length);
    }, 4000); // Change every 4 seconds
    
    const boxInterval = setInterval(() => {
      setBoxImageIndex((prev) => (prev + 1) % boxImages.length);
    }, 4000); // Change every 4 seconds
    
    return () => {
      clearInterval(industrialInterval);
      clearInterval(boxInterval);
    };
  }, []);

  const categories = [
    {
      id: 'retail',
      icon: Package,
      image: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/jur6btnn_ChatGPT%20Image%20Feb%2018%2C%202026%2C%2006_49_53%20AM.png',
      gradient: 'from-blue-500/30 to-blue-600/30',
      href: '/retail'
    },
    {
      id: 'bulk',
      icon: Droplet,
      image: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/v6abczh1_ChatGPT%20Image%20Feb%2018%2C%202026%2C%2006_53_42%20AM.png',
      gradient: 'from-orange-500/30 to-orange-600/30',
      href: '/olion-vrac'
    },
    {
      id: 'industrial',
      icon: Building2,
      image: industrialImages[industrialImageIndex],
      isAnimated: true,
      gradient: 'from-green-500/30 to-green-600/30',
      href: '/industriel'
    },
    {
      id: 'box',
      icon: Box,
      image: boxImages[boxImageIndex],
      isAnimated: true,
      gradient: 'from-purple-500/30 to-purple-600/30',
      href: '/olion-box'
    }
  ];

  // Product images for animated carousel
  const productImages = [
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/pzx8vnew_472787592_493635326675277_1385196126698703798_n.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/r0bf6hrx_472925450_1872844036578094_9173580537994047888_n.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/dzuu48cx_473955635_627617339774511_7011999915038796185_n.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/lip3gjam_474184174_1253559032402271_5814189574520743478_n.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/zs3xungz_474258256_927926469429695_8539267517612110903_n.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/wycqgc3x_ChatGPT%20Image%20F2.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/kvhr6hp3_ChatGPT%20Image%20F22.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/gt6as0uv_ChatGPT%20Image%20F222.png',
    'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/3uiebs73_ChatGPT%20Image%20F2222.png'
  ];

  const scrollToCatalog = () => {
    window.location.href = '/catalogue';
  };

  return (
    <section id="produits" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 ${direction === 'rtl' ? 'text-right' : ''}`}>
          <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full text-sm font-semibold">
            {t('categories.title')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-6 mb-4">
            {t('categories.subtitle')}
          </h2>
        </div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {categories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <a
                key={category.id}
                href={category.href}
                data-testid={`category-card-${category.id}`}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 border border-gray-100 cursor-pointer block"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={t(`categories.${category.id}.title`)}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-500 ${category.isAnimated ? 'transition-opacity duration-1000' : ''}`}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient} opacity-25 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                    <IconComponent className="w-6 h-6 text-blue-600" />
                  </div>
                </div>

                {/* Content */}
                <div className={`p-6 ${direction === 'rtl' ? 'text-right' : ''}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {t(`categories.${category.id}.title`)}
                  </h3>
                  <p className="text-sm text-blue-600 font-semibold mb-3">
                    {t(`categories.${category.id}.subtitle`)}
                  </p>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {t(`categories.${category.id}.description`)}
                  </p>
                </div>
              </a>
            );
          })}
        </div>

        {/* Animated Product Carousel */}
        <div className="mb-12 overflow-hidden relative">
          <div className="flex animate-marquee gap-12">
            {/* First set of products */}
            {productImages.map((product, index) => (
              <div key={`first-${index}`} className="flex-shrink-0 w-24 flex items-center justify-center">
                <img
                  src={product}
                  alt={`Olion Product ${index + 1}`}
                  className="h-32 w-auto object-contain hover:scale-110 transition-transform duration-300 drop-shadow-lg"
                />
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {productImages.map((product, index) => (
              <div key={`second-${index}`} className="flex-shrink-0 w-24 flex items-center justify-center">
                <img
                  src={product}
                  alt={`Olion Product ${index + 1}`}
                  className="h-32 w-auto object-contain hover:scale-110 transition-transform duration-300 drop-shadow-lg"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center space-y-4">
          <div className="flex flex-wrap gap-4 justify-center items-center">
            <Button
              onClick={scrollToCatalog}
              data-testid="cta-catalogue-btn"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-7 rounded-full text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              📖 {t('catalog.title')} - {t('catalog.viewAll')} →
            </Button>
            <DownloadCatalogButton
              language={language}
              testid="cta-download-pdf-home"
              className="px-8 py-6 text-base rounded-full"
            />
          </div>
          <p className="text-gray-500 text-sm">70+ {t('catalog.availableProducts')}</p>

          <div className="pt-4">
            <a
              href="https://olion.odoo.com/shop"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-shop-btn"
              className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-10 py-7 rounded-full text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              🛒 {t('nav.shop')} - {t('catalog.buyNow')} →
            </a>
            <p className="text-gray-500 mt-4 text-sm">{t('catalog.shopDesc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
